import { useRef, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  Skeleton,
} from "@mui/material";
import DashboardDropdown from "./DashboardDropdown";

const HEADER_ROW1_HEIGHT = 48;
const HEADER_ROW2_HEIGHT = 48;

const TitleStyle = {
  fontSize: "16px",
  lineHeight: "160%",
  color: "#4D5F6E",
  fontWeight: 600,
};

const TimeStyle = {
  fontSize: "12px",
  lineHeight: "160%",
  color: "#667F93",
  fontWeight: 400,
};

const IconBG = {
  background: "#F8FBFC",
  height: "40px",
  width: "40px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": { background: "#C2E0FF" },
};

const cellSx = {
  fontSize: "14px",
  py: 0.8,
  px: 1.5,
  borderBottom: "1px solid #D9E1E7",
  borderRight: "1px solid #D9E1E7",
  color: "#1A2025",
};

const headerCellSx = {
  fontWeight: 700,
  py: 1,
  px: 1.5,
  whiteSpace: "nowrap",
  borderBottom: "1px solid #D9E1E7",
  borderRight: "1px solid #D9E1E7",
  height: HEADER_ROW1_HEIGHT,
  boxSizing: "border-box",
  textAlign: "center",
  fontSize: "12px",
  lineHeight: "120%",
  color: "#fff",
};

export default function OrderOverviewTable({
  title,
  refreshTime,
  icons,
  headerConfig = [],
  rows = [],
  colors = {},
  onRefresh,
  showViewOptions = true,
  showDashboardDropdown = true,
  showRefresh = true,
  showChat = true,
  showExport = true,
  showExpand = true,
  dashboardData = [],
  ShowDragDrop = true,
  showDelete = true,
  onDelete,
}) {
  const tableContainerRef = useRef(null);
  const [activeView, setActiveView] = useState("table");
  const [openExpand, setOpenExpand] = useState(false);
  const [loading, setLoading] = useState(false);

  const viewOptions = [
    { key: "table", icon: icons.table },
    { key: "graph", icon: icons.graph },
  ];

  const leafHeaderCells =
    headerConfig[1]?.cells?.length > 0
      ? headerConfig[1].cells
      : headerConfig[0]?.cells ?? [];

  const totalWidth = leafHeaderCells.reduce(
    (sum, cell) => sum + (cell.width ?? 120),
    0,
  );

  const getHeaderBg = (group) =>
    group === "mid"
      ? colors.headerMid
      : group === "light"
        ? colors.headerLight
        : colors.headerDark;

  const getHeaderBorderColor = (group) =>
    group === "mid"
      ? colors.borderMid
      : group === "light"
        ? colors.borderLight
        : colors.borderDark;

  const getHeaderTextColor = (group) =>
    group === "mid"
      ? colors.textMid
      : group === "light"
        ? colors.textLight
        : colors.textDark;

  const iconStyle = (isActive) => ({
    height: "28px",
    width: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: isActive ? "#fff" : "transparent",
    transition: "all 0.3s ease",
    border: isActive ? "1px solid #D9E1E7" : "none",
    "&:hover": { backgroundColor: isActive ? "#fff" : "#C2E0FF" },
  });

  const handleRefresh = () => {
    setLoading(true);

    Promise.resolve(onRefresh?.())
      .catch((err) => {
        console.error("Refresh error:", err);
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 300); // small delay so skeleton is visible
      });
  };

  // --- Reusable sub-components ---

  const Toolbar = ({ onExpandClick, expandIcon }) => (
    <Box display="flex" justifyContent="space-between" sx={{ mb: 2 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Box display="flex" gap={1} alignItems="center">
          {showDelete && icons.delete && (
            <Box
              sx={{ ...IconBG, cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
            >
              <Box component="img" src={icons.delete} height={24} />
            </Box>
          )}
          <Typography sx={TitleStyle}>{title}</Typography>
        </Box>
        {showRefresh && (
          <>
            <Box sx={{ borderRight: "1px solid #B3C5D4", height: "20px" }} />
            <Typography sx={TimeStyle}>{refreshTime}</Typography>
          </>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap="4px">
        {showViewOptions && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              border: "1px solid #D9E1E7",
              borderRadius: "8px",
              p: "4px",
              background: "#F8FBFC",
            }}
          >
            {viewOptions.map(
              (item) =>
                item.icon && (
                  <Box
                    key={item.key}
                    onClick={() => setActiveView(item.key)}
                    sx={iconStyle(activeView === item.key)}
                  >
                    <Box
                      component="img"
                      src={item.icon}
                      width={24}
                      height={24}
                    />
                  </Box>
                ),
            )}
          </Box>
        )}
        {showDashboardDropdown && (
          <DashboardDropdown initialDashboards={dashboardData} />
        )}
        {showRefresh && icons.refresh && (
          <Box sx={{ ...IconBG, cursor: "pointer" }} onClick={handleRefresh}>
            <Box component="img" src={icons.refresh} height={24} />
          </Box>
        )}
        {showChat && icons.chat && (
          <Box sx={IconBG}>
            <Box component="img" src={icons.chat} height={24} />
          </Box>
        )}
        {showExport && icons.export && (
          <Box sx={IconBG}>
            <Box component="img" src={icons.export} height={24} />
          </Box>
        )}
        {showExpand && expandIcon && (
          <Box sx={{ ...IconBG, cursor: "pointer" }} onClick={onExpandClick}>
            <Box component="img" src={expandIcon} height={24} />
          </Box>
        )}
        {ShowDragDrop && icons.DragDrop && (
          <Box sx={IconBG}>
            <Box component="img" src={icons.DragDrop} height={24} />
          </Box>
        )}
      </Box>
    </Box>
  );
const roundup = (value, min = 0, digits = 2) => {
  if (value === null || value === undefined) return min;
  return Number(value).toFixed(digits);
};
  const TableContent = () => (
    <TableContainer
      ref={tableContainerRef}
      component={Paper}
      elevation={0}
      sx={{ border: "1px solid #D9E1E7", maxHeight: "320px" }}
    >
      <Table
        stickyHeader
        sx={{
          minWidth: totalWidth,
          width: leafHeaderCells.length <= 5 ? "auto" : "100%",
          borderCollapse: "separate",
        }}
      >
        <TableHead>
          {headerConfig.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.cells.map((cell, i) => {
                const group = cell.parent ?? cell.group;
                return (
                  <TableCell
                    key={i}
                    rowSpan={cell.rowSpan}
                    colSpan={cell.colSpan}
                    sx={{
                      ...headerCellSx,
                      bgcolor: getHeaderBg(group),
                      color: getHeaderTextColor(group),
                      width: cell.width,
                      position: "sticky",
                      top: rowIndex === 0 ? 0 : HEADER_ROW1_HEIGHT,
                      zIndex: rowIndex === 0 ? 7 : 6,
                      height:
                        rowIndex === 0
                          ? HEADER_ROW1_HEIGHT
                          : HEADER_ROW2_HEIGHT,
                      ...(group && {
                        borderRight: `1px solid ${getHeaderBorderColor(group)}`,
                        borderBottom: `1px solid ${getHeaderBorderColor(group)}`,
                      }),
                    }}
                  >
                    {cell.label}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {loading
            ? rows.map((row) => (
                <TableRow key={row.id}>
                  {row.cells.map((cell, i) => (
                    <TableCell key={i} sx={{ width: cell.width }}>
                      <Skeleton variant="text" height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : rows.map((row) => (
                <TableRow key={row.id}>
                  {row.cells.map((cell, i) => (
                    <TableCell key={i} sx={{ ...cellSx, width: cell.width }}>
                      {!isNaN(cell.value)
                        ? roundup(Number(cell.value), 0, 2)
                        : cell.value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const GraphPlaceholder = () => (
    <Box
      sx={{
        height: "320px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        px: 4,
      }}
    >
      {loading ? (
        <>
          <Skeleton variant="text" width={160} height={30} />
          <Box
            sx={{
              width: "100%",
              maxWidth: "600px",
              height: "200px",
              display: "flex",
              alignItems: "flex-end",
              gap: 2,
            }}
          >
            {Array.from({ length: 7 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                width="100%"
                height={Math.random() * 150 + 50}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
        </>
      ) : (
        <Typography sx={{ fontWeight: 400 }}>Graph View</Typography>
      )}
    </Box>
  );
  // --- Render ---

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 4,
        p: "20px",
        border: "1px solid #DFDFDF",
      }}
    >
      <Toolbar
        onExpandClick={() => setOpenExpand(true)}
        expandIcon={icons.expand}
      />
      {activeView === "table" ? <TableContent /> : <GraphPlaceholder />}

      <Dialog
        open={openExpand}
        onClose={() => setOpenExpand(false)}
        fullWidth
        maxWidth="lg"
      >
        <Box sx={{ p: 3 }}>
          <Toolbar
            onExpandClick={() => setOpenExpand(false)}
            expandIcon={icons.expandless}
          />
          {activeView === "table" ? <TableContent /> : <GraphPlaceholder />}
        </Box>
      </Dialog>
    </Box>
  );
}
